import { define, emit, EVENTS } from './theme.js';
class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.life = new AbortController();
    this.addEventListener('change', event => this.update(event), { signal: this.life.signal });
  }
  disconnectedCallback() { this.life.abort(); this.request?.abort(); }
  async update(event) {
    this.request?.abort();
    const request = this.request = new AbortController();
    const section = this.getAttribute('section');
    const root = document.getElementById(`Product-${section}`);
    const form = document.getElementById(this.getAttribute('for'));
    if (!root || !form) return;
    const values = [...this.querySelectorAll('[data-option]')].map(option => (option.querySelector('select')?.selectedOptions[0] || option.querySelector('input:checked'))?.dataset.optionValueId);
    const changed = event?.target.selectedOptions?.[0] || event?.target;
    const destination = changed?.dataset.productUrl || this.getAttribute('url');
    const url = new URL(destination, location.origin);
    const combined = url.pathname !== new URL(this.getAttribute('url'), location.origin).pathname;
    if (combined && !this.hasAttribute('update-url')) { location.assign(url); return; }
    if (!combined) url.searchParams.set('option_values', values.join(','));
    const plan = form.querySelector('[name="selling_plan"]:checked')?.value;
    if (plan) url.searchParams.set('selling_plan', plan);
    const publicURL = new URL(url);
    if (!combined) url.searchParams.set('section_id', section);
    root.dataset.variantLoading = 'true';
    root.querySelectorAll('[data-add-to-cart], [data-sticky-add]').forEach(button => { button.disabled = true; });
    const payment = form.querySelector('.shopify-payment-button');
    if (payment) payment.hidden = true;
    const focusId = document.activeElement?.id;
    try {
      const response = await fetch(url, { signal: request.signal });
      if (!response.ok) throw new Error('Variant render failed');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (request.signal.aborted || !this.isConnected) return;
      if (combined) {
        const replacement = doc.querySelector('main');
        const currentMain = root.closest('main');
        if (!replacement || !currentMain || !replacement.querySelector(`[id^="Product-"]`)) throw new Error('Missing product content');
        currentMain.replaceWith(replacement);
        replacement.querySelectorAll('script[type="module"][src]').forEach(script => {
          const active = document.createElement('script');
          active.type = 'module';
          active.src = script.src;
          script.replaceWith(active);
        });
        document.title = doc.title;
        for (const selector of ['link[rel="canonical"]', 'meta[name="description"]', 'meta[property^="og:"]', 'meta[name^="twitter:"]', 'script[type="application/ld+json"]']) {
          document.head.querySelectorAll(selector).forEach(node => node.remove());
          doc.head.querySelectorAll(selector).forEach(node => document.head.append(node));
        }
        history.replaceState({}, '', publicURL);
        replacement.querySelector('h1')?.setAttribute('tabindex', '-1');
        replacement.querySelector('h1')?.focus({ preventScroll: true });
        window.Shopify?.PaymentButton?.init();
        return;
      }
      const next = doc.getElementById(this.id);
      if (!next) throw new Error('Missing variant picker');
      const variant = JSON.parse(next.querySelector('[data-selected-variant]').textContent);
      this.innerHTML = next.innerHTML;
      const id = form.querySelector('[data-variant-id]');
      id.replaceChildren(new Option(variant?.title || '', variant?.id || '', true, true));
      id.disabled = !variant;
      for (const prefix of ['Price', 'Sku', 'SellingPlans', 'StickyPrice', 'StickyVariant', 'Inventory', 'Installments', 'AddToCartPrice', 'StickyButtonPrice', 'PackBadge', 'QuantityHelper']) {
        const target = document.getElementById(`${prefix}-${section}`);
        const source = doc.getElementById(`${prefix}-${section}`);
        if (target && source) target.innerHTML = source.innerHTML;
      }
      // Any element marked data-variant-refresh (with a stable id) is refreshed from the new render too,
      // so store blocks that read the selected variant stay in step without adding ids to the list above.
      doc.querySelectorAll('[data-variant-refresh][id]').forEach(source => {
        const target = document.getElementById(source.id);
        if (target) target.innerHTML = source.innerHTML;
      });
      const quantity = document.getElementById(`Quantity-${section}`);
      const nextQuantity = doc.getElementById(`Quantity-${section}`);
      if (quantity && nextQuantity) {
        for (const key of ['min', 'max', 'step']) {
          if (nextQuantity.hasAttribute(key)) quantity.setAttribute(key, nextQuantity.getAttribute(key));
          else quantity.removeAttribute(key);
        }
        quantity.value = quantity.min || '1';
      }
      const rules = document.getElementById(`QuantityRules-${section}`);
      const nextRules = doc.getElementById(`QuantityRules-${section}`);
      if (rules && nextRules) rules.innerHTML = nextRules.innerHTML;
      window.Shopify?.PaymentButton?.init();
      root.querySelectorAll('[data-add-to-cart], [data-sticky-add]').forEach(button => {
        button.disabled = !variant?.available;
        (button.querySelector('[data-add-to-cart-text]') || button).textContent = !variant ? button.dataset.textUnavailable : variant.available ? button.dataset.textAdd : button.dataset.textSoldOut;
      });
      if (payment) payment.hidden = !variant?.available;
      if (this.hasAttribute('update-url')) {
        if (variant) { publicURL.searchParams.delete('option_values'); publicURL.searchParams.set('variant', variant.id); }
        history.replaceState({}, '', publicURL);
      }
      emit(EVENTS.variantChange, { variant, product: this.getAttribute('product-id'), section });
      document.getElementById(focusId)?.focus({ preventScroll: true });
    } catch (error) {
      if (error.name !== 'AbortError') location.assign(publicURL);
    } finally { if (this.request === request) delete root.dataset.variantLoading; }
  }
}
define('variant-picker', VariantPicker);
